import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { AlertCircle, CheckCircle, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface ToolCallInfo {
  toolName: string;
  argsText: string;
  result: string | Record<string, unknown> | undefined;
  status: "loading" | "success" | "error";
}

interface SystemInteractionGroupProps {
  toolCalls: ToolCallInfo[];
}

/**
 * Groups multiple tool calls into a single expandable component
 * Shows user-friendly summary with technical details available on expand
 */
export const SystemInteractionGroup: React.FC<SystemInteractionGroupProps> = ({ toolCalls }) => {
  const { t } = useTranslation("ai-chat");
  const [isExpanded, setIsExpanded] = useState(false);

  const operationsCount = toolCalls.length;
  const loadingCount = toolCalls.filter((tc) => tc.status === "loading").length;
  const errorCount = toolCalls.filter((tc) => tc.status === "error").length;
  const isComplete = loadingCount === 0;
  const hasErrors = errorCount > 0;

  // Determine overall status
  const getStatusText = () => {
    if (!isComplete) {
      return t("systemInteraction.working");
    }
    if (hasErrors) {
      return t("systemInteraction.withErrors");
    }
    return t("systemInteraction.complete");
  };

  const getStatusIcon = () => {
    if (!isComplete) {
      return <Loader2 className="size-4 animate-spin text-blue-600" />;
    }
    if (hasErrors) {
      return <AlertCircle className="size-4 text-amber-600" />;
    }
    return <CheckCircle className="size-4 text-green-600" />;
  };

  const operationsLabel =
    operationsCount === 1
      ? t("systemInteraction.operations")
      : t("systemInteraction.operations_plural");

  return (
    <Card className="my-2 max-w-2xl border-muted">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm font-medium">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <span>{getStatusText()}</span>
            <Badge variant="secondary" className="ml-2 text-xs">
              {operationsCount} {operationsLabel}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="h-7 px-2 text-xs"
          >
            {isExpanded ? (
              <>
                {t("systemInteraction.hideDetails")}
                <ChevronUp className="ml-1 size-3" />
              </>
            ) : (
              <>
                {t("systemInteraction.showDetails")}
                <ChevronDown className="ml-1 size-3" />
              </>
            )}
          </Button>
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-3 pt-0">
          <div className="rounded-md border border-muted bg-muted/30 p-3">
            <p className="mb-2 text-xs font-semibold text-muted-foreground">System Operations:</p>
            <div className="space-y-3">
              {toolCalls.map((toolCall, index) => (
                <div
                  key={`${toolCall.toolName}-${index}`}
                  className="space-y-1.5 border-l-2 border-muted-foreground/20 pl-3"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      {index + 1}. {toolCall.toolName}
                    </span>
                    {toolCall.status === "loading" && (
                      <Loader2 className="size-3 animate-spin text-blue-600" />
                    )}
                    {toolCall.status === "success" && (
                      <CheckCircle className="size-3 text-green-600" />
                    )}
                    {toolCall.status === "error" && <AlertCircle className="size-3 text-red-600" />}
                  </div>

                  {/* Parameters */}
                  {toolCall.argsText && (
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium text-muted-foreground/70 hover:text-muted-foreground">
                        Parameters
                      </summary>
                      <pre className="mt-1 overflow-x-auto rounded bg-background/50 p-2 text-xs">
                        {toolCall.argsText}
                      </pre>
                    </details>
                  )}

                  {/* Result */}
                  {toolCall.result !== undefined && (
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium text-muted-foreground/70 hover:text-muted-foreground">
                        Result
                      </summary>
                      <pre className="mt-1 overflow-x-auto rounded bg-background/50 p-2 text-xs">
                        {typeof toolCall.result === "string"
                          ? toolCall.result
                          : JSON.stringify(toolCall.result, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

/**
 * Wrapper component that collects tool calls and renders them as a group
 * This is used as the Fallback for all tool calls in the thread
 */
export const SystemInteractionToolFallback: ToolCallMessagePartComponent = ({
  toolName,
  argsText,
  result,
}) => {
  const { t } = useTranslation("ai-chat");

  // Parse result to determine status
  let status: "loading" | "success" | "error" = "loading";

  if (result !== undefined) {
    try {
      if (typeof result === "string") {
        const parsed = JSON.parse(result) as { error?: string };
        status = parsed?.error ? "error" : "success";
      } else {
        const resultObj = result as { error?: string };
        status = resultObj?.error ? "error" : "success";
      }
    } catch {
      status = "success";
    }
  }

  const hasResult = result !== undefined;
  const displayName = toolName.replace(/_/g, " ");

  // For individual tool fallback, show a badge with tooltip containing params and result
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={
            status === "loading" ? "secondary" : status === "success" ? "default" : "destructive"
          }
          className="text-[10px] py-0 h-5 cursor-help"
        >
          {status === "loading" && <Loader2 className="mr-1 size-2.5 animate-spin" />}
          {status === "success" && <CheckCircle className="mr-1 size-2.5" />}
          {status === "error" && <AlertCircle className="mr-1 size-2.5" />}
          <span className="max-w-[120px] truncate">{displayName}</span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-md p-3">
        <div className="space-y-2 text-left">
          <div>
            <p className="mb-1 text-xs font-semibold opacity-70">{t("toolFallback.parameters")}</p>
            <pre className="overflow-x-auto text-xs">{argsText || "{}"}</pre>
          </div>
          {hasResult && (
            <div className="border-t border-primary-foreground/20 pt-2">
              <p className="mb-1 text-xs font-semibold opacity-70">{t("toolFallback.result")}</p>
              <pre className="overflow-x-auto text-xs">
                {typeof result === "string" ? result : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
